import { format, parseISO, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { Activity, AlertCircle, CalendarClock, ChevronRight } from 'lucide-react';
import { Card, Page } from '../../components/Page';
import { firstName, useProfile } from '../../data/profile';
import { useSchedules } from '../../data/schedule';
import { useSessions, useSessionVitalsSummary } from '../../data/sessions';
import { useCanWrite } from '../../account/useCanWrite';
import { nextSession, plannedSessions } from '../../lib/schedule';
import { formatKg, interdialyticGains, unloggedPlanned, weightRemoved } from '../../lib/sessions';
import { describeWhen } from '../schedule/describe';
import { localToday } from '../sessions/fields';
import { AccountAlerts, FluidTodayCard, LabAlert, MedsTodayCard, WeightTodayCard } from './TodayCards';

function greeting(hour: number) {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-clinical-muted">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

export function TodayPage() {
  const now = new Date();
  const today = localToday();
  const canWrite = useCanWrite();
  const { profile } = useProfile();
  const { schedules, exceptions, isLoading } = useSchedules();
  const { sessions } = useSessions();
  const vitals = useSessionVitalsSummary();
  const name = firstName(profile);

  const inProgress = sessions.find((session) => session.status === 'in_progress');
  const loggedDates = new Set(sessions.map((session) => session.session_date));
  const next = isLoading ? null : nextSession(schedules, exceptions, now, loggedDates);
  const last = sessions.find((session) => session.status === 'completed' || session.status === 'ended_early');
  const gain = last ? interdialyticGains(sessions).get(last.id) : undefined;
  const lastBp = last ? vitals.get(last.id) : undefined;
  const notLogged = isLoading ? [] : unloggedPlanned(plannedSessions(schedules, exceptions, subDays(now, 7), subDays(now, 1)), sessions, today);

  return (
    <Page title={format(now, 'EEEE, d MMMM')}>
      <p className="-mt-3 text-sm text-clinical-muted">
        {greeting(now.getHours())}
        {name ? `, ${name}` : ''}.
      </p>

      {inProgress ? (
        <Link
          to={`/sessions/${inProgress.id}`}
          className="focus-ring flex items-center gap-3 rounded-xl border border-blue-300/60 bg-blue-500/10 p-4 text-sm"
        >
          <Activity className="h-6 w-6 shrink-0 text-blue-700 dark:text-blue-300" />
          <span className="flex-1">
            <span className="block font-semibold">Session in progress</span>
            <span className="text-clinical-muted">Started {inProgress.start_time ?? format(parseISO(inProgress.session_date), 'EEE d MMM')}. Tap to continue.</span>
          </span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : null}

      <AccountAlerts />
      <LabAlert />

      {notLogged.length && canWrite ? (
        <Link to="/sessions" className="focus-ring flex items-center gap-3 rounded-xl border border-amber-300/60 bg-amber-500/10 p-4 text-sm">
          <AlertCircle className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" />
          <span className="flex-1">
            {notLogged.length === 1
              ? `Your session on ${format(parseISO(notLogged[0].date), 'EEEE')} isn’t logged yet.`
              : `${notLogged.length} sessions this week aren’t logged yet.`}
          </span>
          <ChevronRight className="h-4 w-4" />
        </Link>
      ) : null}

      <Card title="Next session">
        {isLoading ? null : next ? (
          <div className="flex items-center gap-3">
            <CalendarClock className="h-6 w-6 shrink-0 text-clinical-primary" />
            <div className="flex-1">
              <p className="text-lg font-semibold">{describeWhen(next)}</p>
              {next.location ? <p className="text-sm text-clinical-muted">{next.location}</p> : null}
            </div>
            {next.date === today && canWrite && !inProgress ? (
              <Link to={`/sessions/new?date=${today}`} className="focus-ring rounded-lg bg-clinical-primary px-3 py-2 text-sm font-semibold text-white">
                Start
              </Link>
            ) : null}
          </div>
        ) : (
          <Link to="/sessions" className="flex items-center justify-between text-sm text-clinical-primary">
            Add your dialysis schedule <ChevronRight className="h-4 w-4" />
          </Link>
        )}
      </Card>

      {last ? (
        <Link to={`/sessions/${last.id}`} className="focus-ring block rounded-xl">
          <Card title={`Last session · ${format(parseISO(last.session_date), 'EEE d MMM')}`}>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Removed" value={formatKg(weightRemoved(last))} />
              <Stat label="Gain before it" value={gain == null ? '–' : formatKg(gain, true)} />
              <Stat
                label="BP before → after"
                value={lastBp?.pre?.bp_systolic != null ? `${lastBp.pre.bp_systolic}/${lastBp.pre.bp_diastolic} → ${lastBp.post?.bp_systolic ?? '…'}${lastBp.post?.bp_diastolic != null ? `/${lastBp.post.bp_diastolic}` : ''}` : '–'}
              />
            </div>
          </Card>
        </Link>
      ) : null}

      <MedsTodayCard />
      <FluidTodayCard />
      <WeightTodayCard />
    </Page>
  );
}
